import { useState } from 'react';
import useTranslation from 'next-translate/useTranslation';

import clsx from 'clsx';
import { useAtom } from 'jotai';

import { useFindSPFromMessageId } from '@/components/pages/conversations/SocialInbox/hooks/useFindSPFromMessage';
import { useSocialInboxDataSource } from '@/components/pages/conversations/SocialInbox/hooks/useSocialInboxDataSource';

import { post } from '@/utils/client';
import { injectEndpointUrlParams } from '@/utils/injectEndpointUrlParams';

import { MessageType } from '@/types/messageType';

import {
    COMMENT_MARK_AS_IMPORTANT,
    COMMENT_MARK_AS_RESOLVED,
    COMMENT_MARK_AS_UNIMPORTANT,
    COMMENT_MARK_AS_UNRESOLVED
} from '@/lib/routes/apiEndpoints';

import { openedMessageAtom } from '@/components/pages/conversations/SocialInbox/atoms';

import Button from '@/components/base/Button';
import Icon from '@/components/base/Icon';
import Loader from '@/components/base/Loader';
import { ProfileItem } from '@/components/composite/SelectedProfiles/ProfilesList/ProfileItem';

export const SocialInboxConversationHeader = () => {
    const { t } = useTranslation('conversations');
    const [openedMessage, setOpenedMessage] = useAtom(openedMessageAtom);
    const { mutateMessages } = useSocialInboxDataSource();

    const [resolvedLoading, setResolvedLoading] = useState(false);
    const [importantLoading, setImportantLoading] = useState(false);

    /* Retrieve message and SP */

    const { socialProfile } = useFindSPFromMessageId({ message: openedMessage as MessageType });
    const isMention = 'mentioned_in' in openedMessage;

    /* Methods */

    const markAsResolved = async () => {
        if (!('is_resolved' in openedMessage)) return;

        setResolvedLoading(true);
        try {
            await post(
                injectEndpointUrlParams(
                    openedMessage.is_resolved
                        ? COMMENT_MARK_AS_UNRESOLVED
                        : COMMENT_MARK_AS_RESOLVED,
                    {
                        SPID: socialProfile.id,
                        mediaID: openedMessage.media_id,
                        commentID: openedMessage.id
                    }
                )
            );

            mutateMessages((prev) =>
                prev.map((message) => {
                    if (message.id === openedMessage.id && 'is_resolved' in message) {
                        return { ...message, is_resolved: !message.is_resolved };
                    }
                    return message;
                })
            );
        } catch (e) {
            console.error(e);
        }
        setResolvedLoading(false);
    };

    const markAsImportant = async () => {
        if (!('is_important' in openedMessage)) return;

        setImportantLoading(true);
        try {
            await post(
                injectEndpointUrlParams(
                    openedMessage.is_important
                        ? COMMENT_MARK_AS_UNIMPORTANT
                        : COMMENT_MARK_AS_IMPORTANT,
                    {
                        SPID: socialProfile.id,
                        mediaID: openedMessage.media_id,
                        commentID: openedMessage.id
                    }
                )
            );

            mutateMessages((prev) =>
                prev.map((message) => {
                    if (message.id === openedMessage.id && 'is_important' in message) {
                        return { ...message, is_important: !message.is_important };
                    }
                    return message;
                })
            );
        } catch (e) {
            console.error(e);
        }
        setImportantLoading(false);
    };

    const getSocialInboxTypeSubtitle = () => {
        if (isMention) {
            return t('tabs.mention');
        }
        return t('social_inbox.comments_on');
    };

    /* Widget */

    if (!openedMessage) return null;

    return (
        <div className="flex items-center justify-between border-b-[1px] border-icoGray-300 p-4 dark:border-icoDarkMode-beton sm:px-10 sm:py-5">
            <div className="flex h-full grow items-center gap-5">
                <Button
                    variant="neutral"
                    className="!min-w-0 !px-0 lg:hidden"
                    onClick={() => setOpenedMessage(null)}>
                    <Icon icon="chevron-left" />
                </Button>

                <ProfileItem size="base" profile={socialProfile} />

                <div className="flex flex-col">
                    <span className="font-GMedium text-xs text-icoGray-500 dark:text-icoDarkMode-wolf">
                        {getSocialInboxTypeSubtitle()}
                    </span>
                    <span className="font-GSemiBold text-sm text-icoBlack dark:text-white">
                        {socialProfile?.slug}
                    </span>
                </div>
            </div>

            {!isMention && (
                <div className="hidden items-center space-x-5 sm:flex">
                    <div className="flex size-4 items-center justify-center">
                        {!resolvedLoading ? (
                            <Icon
                                onClick={markAsResolved}
                                icon="check-circle-outline"
                                className={clsx(
                                    'text-12 cursor-pointer',
                                    'is_resolved' in openedMessage && openedMessage?.is_resolved
                                        ? 'text-icoBlue'
                                        : 'text-icoBlack'
                                )}
                            />
                        ) : (
                            <Loader className="size-4 text-icoBlack" />
                        )}
                    </div>

                    <div className="flex size-4 items-center justify-center">
                        {!importantLoading ? (
                            <Icon
                                icon="flag"
                                className={clsx(
                                    'text-12 cursor-pointer',
                                    'is_important' in openedMessage && openedMessage?.is_important
                                        ? 'text-icoBlue'
                                        : 'text-icoBlack'
                                )}
                                onClick={markAsImportant}
                            />
                        ) : (
                            <Loader className="size-4 text-icoBlack dark:text-white" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
